import {
  FileAddOutlined,
  FileSearchOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, Card, Form, notification, Skeleton, Table, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GET_PROCEEDINGS_QUERY } from './ProceedingListConstants';
import SearchComponent from './SearchProceedingComponent';
import StatusIndicator from './CustomComponents/StatusIndicator';
import CustomTooltip from './CustomComponents/CustomTooltip';
interface Proceeding {
  id: string;
  referralDate: string;
  isJoint: boolean;
  status: string;
  chcReceivedDate: string;
  proceedingId: number;
  hearingStartDate: string;
}

const ProceedingList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [showSearch, setShowSearch] = useState(false);
  const [filter, setFilter] = useState({});
  const isAuthenticated = useIsAuthenticated();
  const partiesTooltipDescription =
    'Those who have been granted participation in the hearing are identified in this list of parties. In rare circumstances, non-participants may be requested to provide information to a hearing panel and are thus listed here';
  const statusTooltipDescription =
    'In-progress means the proceeding or hearing is active, adjourned means the proceeding is paused, cancelled means the proceeding concluded and a hearing was not held, completed means a hearing was held and a hearing decision was issued.';
  const proceedingUpdateTooltipDescription =
    'This information is updated as new activities on a proceeding occur. It only reflects the most current activities. Please see the exhibits for a specific proceeding for more information.';
  const hearingStartDateTooltipDescription =
    'If the hearing start date is blank, this means a hearing date is not scheduled at this time.';
  const {
    data: proceedingsData,
    loading,
    error,
    refetch,
  } = useQuery(GET_PROCEEDINGS_QUERY, {
    variables: { filter },
  });

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error fetching proceedings',
        description: error.message || 'An unknown error occurred.',
      });
    }
  }, [error]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    refetch({ filter });
  }, [filter]);

  const getEarliestStartDate = (dates: any) => {
    if (!dates || dates.length === 0) return '';
    return dates
      .map((date: any) => dayjs(date.startDate).isValid() ? dayjs(date.startDate) : null)
      .filter((date: any) => date !== null)
      .sort((a : any, b : any) => a - b)[0]
      ?.format('YYYY-MM-DD');
  };

  const proceedings: Proceeding[] =
    proceedingsData?.proceedings?.data.map((proceeding: any) => {
      const hearingStartDate = proceeding.hearingDates && proceeding.hearingDates.length > 0
      ? getEarliestStartDate(proceeding?.hearingDates)
      : '';

      return {
        id: proceeding.id,
        status: proceeding.status,
        proceedingId: proceeding.proceedingId,
        applicantNames: proceeding.applicantNames,
        partiesNames: proceeding.partiesNames,
        description: proceeding.description,
        reportUpdate: proceeding.reportUpdate,
        hearingStartDate,
        key: proceeding.id,
      };
    }) || [];

  const columns = [
    {
      title: 'Proceeding ID',
      dataIndex: 'proceedingId',
      key: 'proceedingId',
      width: 120,
      sorter: (a: any, b: any) => a.proceedingId - b.proceedingId,
      render: (text: any) => (
        <div
          style={{ verticalAlign: 'top', textAlign: 'left', paddingLeft: '20px', height: '100%' }}
        >
          {text}
        </div>
      ),
    },
    {
      title: (
        <>
          Parties/Non-Participants
          <CustomTooltip title={partiesTooltipDescription}></CustomTooltip>
        </>
      ),
      dataIndex: 'partiesNames',
      key: 'partiesNames',
      width: 220,
      render: (names: string[]) => Array.isArray(names) && names?.join(', '),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 220,
    },
    {
      title: (
        <>
          Status
          <CustomTooltip title={statusTooltipDescription}></CustomTooltip>
        </>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      render: (status: string) => <StatusIndicator status={status} />,
    },
    {
      title: (
        <>
          Proceeding Update
          <CustomTooltip title={proceedingUpdateTooltipDescription}></CustomTooltip>
        </>
      ),
      dataIndex: 'reportUpdate',
      key: 'reportUpdate',
      width: 220,
    },
    {
      title: (
        <>
          Hearing Start Date
          <CustomTooltip title={hearingStartDateTooltipDescription}></CustomTooltip>
        </>
      ),
      dataIndex: 'hearingStartDate',
      key: 'hearingStartDate',
      width: 120,
      render: (hearingStartDate: string) =>
        hearingStartDate ? (
          <Tag
            color="transparent"
            style={{
              fontSize: '14px',
              padding: '4px 8px',
              borderRadius: '5px',
              whiteSpace: 'pre-line',
              color: 'black',              
            }}
          >
            {hearingStartDate}
          </Tag>
        ) : (
          ''
        ),
      sorter: (a: any, b: any) => {
        const dateA = a.hearingStartDate ? dayjs(a.hearingStartDate.split('\n')[0]).unix() : 0;
        const dateB = b.hearingStartDate ? dayjs(b.hearingStartDate.split('\n')[0]).unix() : 0;
        return dateA - dateB;
      },
    },
  ];

  const handleRowClick = (record: Proceeding) => {
    navigate(`/hearing/proceeding/${record.proceedingId}`);
  };

  const handleSearch = (values: any) => {
    const filter = {
      ...values,
      createdDate: values.createdDate
        ? { from: values.createdDate[0]?.toISOString(), to: values.createdDate[1]?.toISOString() }
        : undefined,
    };
    setFilter(filter);
    refetch({ filter });
  };

  const handleReset = () => {
    form.resetFields();
    setFilter({});
    refetch();
  };

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        {isAuthenticated ? (
          <>
            <Link to="/hearing/proceeding">
              <Button icon={<FileAddOutlined />} style={{ marginRight: '14px' }}>
                Create Proceeding
              </Button>
            </Link>

            <Button
              icon={
                showSearch ? (
                  <MinusOutlined style={{ fontSize: '14px' }} />
                ) : (
                  <PlusOutlined style={{ fontSize: '14px' }} />
                )
              }
              style={{ marginRight: '10px' }}
              type="primary"
              onClick={() => setShowSearch(!showSearch)}
            >
              {showSearch ? 'Hide Filter Proceedings' : 'Filter Proceedings'}
            </Button>
            <Link to="/hearing/searchDocument">
              <Button icon={<FileSearchOutlined />} type="primary">
                Keyword Search
              </Button>
            </Link>

            {showSearch && (
              <>
                <br />
                <br />
                <SearchComponent
                  form={form}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  onCancel={() => setShowSearch(false)}
                />
              </>
            )}
          </>
        ) : (
          <>
            <Button
              icon={
                showSearch ? (
                  <MinusOutlined style={{ fontSize: '14px' }} />
                ) : (
                  <PlusOutlined style={{ fontSize: '14px' }} />
                )
              }
              style={{ marginRight: '10px' }}
              type="primary"
              onClick={() => setShowSearch(!showSearch)}
            >
              {showSearch ? 'Hide Filter Proceedings' : 'Filter Proceedings'}
            </Button>
            <Link to="/hearing/searchDocument">
              <Button icon={<FileSearchOutlined style={{ fontSize: '14px' }} />} type="primary">
                Keyword Search
              </Button>
            </Link>

            {showSearch && (
              <>
                <br />
                <br />
                <SearchComponent
                  form={form}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  onCancel={() => setShowSearch(false)}
                />
              </>
            )}
          </>
        )}
      </div>
      {loading ? (
        <Skeleton />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexGrow: 10,
          }}
        >
          <Card
            title={
              <div>
                <span className="highlighted-title">Proceedings List</span>
                <p className="card-paragraph">
                  Proceedings are listed in numerical order with in-progress proceedings at the top,
                  followed by adjourned proceedings. Cancelled and completed proceedings are listed
                  at the end. Select a proceeding to view additional information.
                </p>
              </div>
            }
            className="card"
          >
            <Table
              dataSource={proceedings}
              loading={loading}
              columns={columns}
              onRow={(record: Proceeding) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer', verticalAlign: 'top' },
              })}
              rowClassName="clickable-row"
              style={{ textAlign: 'left', verticalAlign: 'top' }}
              tableLayout="fixed"
            />
          </Card>
        </div>
      )}
    </>
  );
};

export default ProceedingList;
